import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {itemUrlSegment, taxonomySegmentsFromBc} from './item-url.utils';
import {firstListingPhotoPath} from './listing-image.utils';
import {promoteToolsSection} from './listing-browse-ui/rightbar/browse-tools-mru';
import {resolveProfileId} from './listing-route.utils';

export interface BrowseRecentSearch {
  id: string;
  summary: string;
  timestamp: string;
  filters: {
    keyword?: string | null;
    location?: string | null;
    minPrice?: number | string | null;
    maxPrice?: number | string | null;
    attributes?: Record<string, string>;
  };
}

export interface BrowseRecentlyViewed {
  itemId: string;
  name: string;
  value: number | string | null;
  /** Raw photo field / first path — resolve with listingThumbnailUrl at display time. */
  photo: string | null;
  route: string[];
  timestamp: string;
}

const SEARCHES_KEY = 'browse_recent_searches_v2';
const VIEWED_KEY = 'browse_recently_viewed_v2';
const LEGACY_SEARCHES_KEY = 'recentSearches';
const LEGACY_VIEWED_KEY = 'recentlyViewed';

@Injectable({providedIn: 'root'})
export class BrowseHistoryService {
  private readonly searchesSubject = new BehaviorSubject<BrowseRecentSearch[]>([]);
  private readonly viewedSubject = new BehaviorSubject<BrowseRecentlyViewed[]>([]);

  readonly recentSearches$ = this.searchesSubject.asObservable();
  readonly recentlyViewed$ = this.viewedSubject.asObservable();

  constructor() {
    this.searchesSubject.next(this.readSearches());
    this.viewedSubject.next(this.readViewed());
  }

  get recentSearches(): BrowseRecentSearch[] {
    return this.searchesSubject.value;
  }

  get recentlyViewed(): BrowseRecentlyViewed[] {
    return this.viewedSubject.value;
  }

  /** Snapshot of active filter criteria worth remembering as a "search". */
  extractSearchFilters(listingFilter: Record<string, unknown> | null | undefined): BrowseRecentSearch['filters'] | null {
    if (!listingFilter) {
      return null;
    }
    const keyword = `${listingFilter['keyword'] ?? ''}`.trim();
    const locationRaw = `${listingFilter['location'] ?? ''}`.trim();
    const location = locationRaw && locationRaw.toLowerCase() !== 'all' ? locationRaw : '';
    const minPrice = Number(listingFilter['minPrice']);
    const maxPrice = Number(listingFilter['maxPrice']);
    const hasPrice = (Number.isFinite(minPrice) && minPrice > 0)
      || (Number.isFinite(maxPrice) && maxPrice > 0 && maxPrice < 150000);

    const attributes: Record<string, string> = {};
    const rawAttrs = listingFilter['attributes'];
    if (rawAttrs && typeof rawAttrs === 'object' && !Array.isArray(rawAttrs)) {
      for (const [key, value] of Object.entries(rawAttrs as Record<string, unknown>)) {
        const text = `${value ?? ''}`.trim();
        if (text) {
          attributes[key] = text;
        }
      }
    }

    if (!keyword && !location && !hasPrice && !Object.keys(attributes).length) {
      return null;
    }

    return {
      keyword: keyword || null,
      location: location || null,
      minPrice: hasPrice && Number.isFinite(minPrice) ? minPrice : null,
      maxPrice: hasPrice && Number.isFinite(maxPrice) ? maxPrice : null,
      attributes,
    };
  }

  summarizeSearchFilters(filters: BrowseRecentSearch['filters']): string {
    const parts: string[] = [];
    if (filters.keyword) {
      parts.push(filters.keyword);
    }
    if (filters.location) {
      parts.push(filters.location);
    }
    const minPrice = Number(filters.minPrice);
    const maxPrice = Number(filters.maxPrice);
    if ((Number.isFinite(minPrice) && minPrice > 0) || (Number.isFinite(maxPrice) && maxPrice > 0 && maxPrice < 150000)) {
      parts.push(`€${Number.isFinite(minPrice) ? minPrice : 0}–€${Number.isFinite(maxPrice) ? maxPrice : 150000}`);
    }
    for (const [code, value] of Object.entries(filters.attributes || {})) {
      if (value) {
        parts.push(`${code}: ${value}`);
      }
    }
    return parts.join(' · ');
  }

  recordSearch(listingFilter: Record<string, unknown> | null | undefined): void {
    const filters = this.extractSearchFilters(listingFilter);
    if (!filters) {
      return;
    }
    const summary = this.summarizeSearchFilters(filters);
    if (!summary) {
      return;
    }
    const next = this.recentSearches.filter(s => s.summary !== summary);
    next.unshift({
      id: `rs_${Date.now()}`,
      summary,
      timestamp: new Date().toISOString(),
      filters,
    });
    this.persistSearches(next.slice(0, 8));
  }

  recordViewed(item: any, fallbackPathSegments?: string[]): void {
    if (!item?.itemId && !item?.itemUrl && !item?.url) {
      return;
    }
    const itemId = String(item.itemId || '');
    const name = `${item.name || ''}`.trim();
    if (!itemId || !name) {
      return;
    }
    const route = this.buildItemRoute(item, fallbackPathSegments);
    if (route.length < 2) {
      return;
    }
    const entry: BrowseRecentlyViewed = {
      itemId,
      name,
      value: item.value ?? null,
      photo: firstListingPhotoPath(item.photo) || (typeof item.photo === 'string' ? item.photo : null),
      route,
      timestamp: new Date().toISOString(),
    };
    const next = this.recentlyViewed.filter(v => v.itemId !== itemId);
    next.unshift(entry);
    this.persistViewed(next.slice(0, 12));
    promoteToolsSection('viewed');
  }

  buildItemRoute(item: any, fallbackPathSegments?: string[]): string[] {
    const rawFallback = (fallbackPathSegments || [])
      .map(s => `${s || ''}`.trim())
      .filter(s => !!s && s !== 'all' && !s.startsWith('all-'));
    const profile = resolveProfileId(rawFallback);
    const fromBc = taxonomySegmentsFromBc(item?.bc);
    // Prefer item taxonomy (bc); otherwise path segments with profile slug stripped.
    const taxonomy = fromBc.length
      ? fromBc
      : (profile ? rawFallback.slice(1) : rawFallback);
    const leaf = itemUrlSegment(item);
    if (!leaf) {
      return [];
    }
    // Keep shop context: /paul-one/smart-home/.../item (not /smart-home/.../item)
    if (profile) {
      return ['/', profile, ...taxonomy, leaf];
    }
    return ['/', ...taxonomy, leaf];
  }

  private readSearches(): BrowseRecentSearch[] {
    const primary = this.readJsonArray(SEARCHES_KEY);
    if (primary.length) {
      return primary.map(e => this.normalizeSearch(e)).filter((e): e is BrowseRecentSearch => !!e);
    }
    // One-time soft migrate from legacy key (drop taxonomy-only / junk entries).
    const legacy = this.readJsonArray(LEGACY_SEARCHES_KEY)
      .map(e => this.normalizeSearch(e))
      .filter((e): e is BrowseRecentSearch => !!e);
    if (legacy.length) {
      this.persistSearches(legacy.slice(0, 8));
    }
    return legacy.slice(0, 8);
  }

  private readViewed(): BrowseRecentlyViewed[] {
    const primary = this.readJsonArray(VIEWED_KEY);
    if (primary.length) {
      return primary.map(e => this.normalizeViewed(e)).filter((e): e is BrowseRecentlyViewed => !!e);
    }
    const legacy = this.readJsonArray(LEGACY_VIEWED_KEY)
      .map(e => this.normalizeViewed(e))
      .filter((e): e is BrowseRecentlyViewed => !!e);
    if (legacy.length) {
      this.persistViewed(legacy.slice(0, 12));
    }
    return legacy.slice(0, 12);
  }

  private normalizeSearch(raw: any): BrowseRecentSearch | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const source = raw.filters && typeof raw.filters === 'object' ? raw.filters : raw;
    const filters = this.extractSearchFilters(source);
    if (!filters) {
      return null;
    }
    const summary = `${raw.summary || ''}`.trim() || this.summarizeSearchFilters(filters);
    if (!summary || summary === 'All listings') {
      return null;
    }
    return {
      id: `${raw.id || `rs_${Date.now()}`}`,
      summary,
      timestamp: `${raw.timestamp || new Date().toISOString()}`,
      filters,
    };
  }

  private normalizeViewed(raw: any): BrowseRecentlyViewed | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const itemId = `${raw.itemId || ''}`.trim();
    const name = `${raw.name || ''}`.trim();
    if (!itemId || !name) {
      return null;
    }
    let route = Array.isArray(raw.route) ? raw.route.map((s: any) => `${s}`).filter(Boolean) : [];
    if (route.length && route[0] !== '/') {
      route = ['/', ...route];
    }
    // Drop entries we cannot navigate (legacy without route).
    if (route.length < 2) {
      return null;
    }
    let photo: string | null = null;
    if (typeof raw.photo === 'string' && raw.photo.trim()) {
      // Prefer raw path; strip absolute/base URL prefixes when present.
      const match = raw.photo.match(/\/(?:static|images|uploads)\/.+$/i) || raw.photo.match(/\/[^/?#]+\/[^/?#].*$/);
      photo = firstListingPhotoPath(raw.photo) || (raw.photo.startsWith('http') ? null : raw.photo);
      if (!photo && match) {
        photo = match[0];
      }
    }
    return {
      itemId,
      name,
      value: raw.value ?? null,
      photo,
      route,
      timestamp: `${raw.timestamp || new Date().toISOString()}`,
    };
  }

  private persistSearches(entries: BrowseRecentSearch[]): void {
    this.searchesSubject.next(entries);
    try {
      localStorage.setItem(SEARCHES_KEY, JSON.stringify(entries));
    } catch {
      // ignore
    }
  }

  private persistViewed(entries: BrowseRecentlyViewed[]): void {
    this.viewedSubject.next(entries);
    try {
      localStorage.setItem(VIEWED_KEY, JSON.stringify(entries));
    } catch {
      // ignore
    }
  }

  private readJsonArray(key: string): any[] {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
